/**
 * 拼图碎片类
 * 管理单个碎片的状态、位置、旋转等
 */
import { Vector2, HexCoord, PieceState, EdgeDirection, ComplexityLevel, Quadrant } from '../types';
import { HexMath } from '../core/HexMath';
import { CoordinateSystem } from '../core/CoordinateSystem';

export class PuzzlePiece {
    public readonly id: string;
    public readonly coord: HexCoord;

    // 当前状态
    private _position: Vector2;
    private _rotation: number;  // 0, 60, 120, 180, 240, 300
    private _isPlaced: boolean;
    private _isInPocket: boolean = false;
    private _groupId: string | null;
    private _dirty: boolean = true; // 初始状态为 dirty

    // 智能布局相关属性
    private _isEdge: boolean = false;
    private _complexityLevel: ComplexityLevel = ComplexityLevel.MEDIUM;
    private _homeQuadrant: Quadrant = Quadrant.TOP_LEFT;

    // 原始正确位置（用于判断是否归位）
    public readonly correctPosition: Vector2;

    // SVG 路径数据
    public readonly pathData: string;

    // 边缘类型
    public readonly edgeTypes: Map<EdgeDirection, 'tab' | 'hole' | 'flat'>;

    constructor(
        coord: HexCoord,
        correctPosition: Vector2,
        pathData: string,
        edgeTypes: Map<EdgeDirection, 'tab' | 'hole' | 'flat'>
    ) {
        this.id = `piece_${coord.u}_${coord.v}`;
        this.coord = coord;
        this.correctPosition = correctPosition;
        this.pathData = pathData;
        this.edgeTypes = edgeTypes;

        this._position = { ...correctPosition };
        this._rotation = 0;
        this._isPlaced = false;
        this._groupId = null;
    }

    // Getters
    get position(): Vector2 {
        return { ...this._position };
    }

    get rotation(): number {
        return this._rotation;
    }

    get isPlaced(): boolean {
        return this._isPlaced;
    }

    get isInPocket(): boolean {
        return this._isInPocket;
    }

    get groupId(): string | null {
        return this._groupId;
    }

    get isDirty(): boolean {
        return this._dirty;
    }

    /**
     * 清除 dirty 标记
     */
    clearDirty(): void {
        this._dirty = false;
    }

    // 智能布局相关 Getters
    get isEdge(): boolean {
        return this._isEdge;
    }

    get complexityLevel(): ComplexityLevel {
        return this._complexityLevel;
    }

    get homeQuadrant(): Quadrant {
        return this._homeQuadrant;
    }

    /**
     * 设置位置
     */
    setPosition(pos: Vector2): void {
        const newPos = CoordinateSystem.roundVector(pos);
        if (newPos.x !== this._position.x || newPos.y !== this._position.y) {
            this._position = newPos;
            this._dirty = true;
        }
    }

    /**
     * 移动（相对位移）
     */
    move(delta: Vector2): void {
        if (delta.x === 0 && delta.y === 0) return;
        this._position = CoordinateSystem.roundVector(HexMath.add(this._position, delta));
        this._dirty = true;
    }

    /**
     * 旋转（60度步进）
     * @param clockwise 是否顺时针
     */
    rotate(clockwise: boolean = true): void {
        const step = clockwise ? 60 : -60;
        this._rotation = HexMath.normalizeAngle(this._rotation + step);
        this._dirty = true;
    }

    /**
     * 设置旋转角度
     */
    setRotation(angle: number): void {
        const newRotation = HexMath.snapAngleTo60(angle);
        if (newRotation !== this._rotation) {
            this._rotation = newRotation;
            this._dirty = true;
        }
    }

    /**
     * 标记为已放置
     */
    place(): void {
        this._isPlaced = true;
        this._position = { ...this.correctPosition };
        this._rotation = 0;
        this._dirty = true;
    }

    /**
     * 取消放置状态（允许重新移动）
     */
    unplace(): void {
        this._isPlaced = false;
        this._dirty = true;
    }

    /**
     * 设置是否在口袋中
     */
    setIsInPocket(inPocket: boolean): void {
        if (this._isInPocket !== inPocket) {
            this._isInPocket = inPocket;
            this._dirty = true;
        }
    }

    /**
     * 设置碎片组
     */
    setGroup(groupId: string | null): void {
        if (this._groupId !== groupId) {
            this._groupId = groupId;
            this._dirty = true;
        }
    }

    /**
     * 设置是否为边缘碎片
     */
    setIsEdge(isEdge: boolean): void {
        this._isEdge = isEdge;
    }

    /**
     * 设置复杂度等级
     */
    setComplexityLevel(level: ComplexityLevel): void {
        this._complexityLevel = level;
    }

    /**
     * 设置所属象限
     */
    setHomeQuadrant(quadrant: Quadrant): void {
        this._homeQuadrant = quadrant;
    }

    /**
     * 检查是否在正确位置（考虑容差）
     */
    isAtCorrectPosition(tolerance: number = 10): boolean {
        if (this._rotation !== 0) return false;

        const dx = Math.abs(this._position.x - this.correctPosition.x);
        const dy = Math.abs(this._position.y - this.correctPosition.y);

        return dx <= tolerance && dy <= tolerance;
    }

    /**
     * 获取变换后的某个方向对应的实际方向
     * 考虑当前旋转角度
     */
    getTransformedDirection(originalDirection: EdgeDirection): EdgeDirection {
        const rotationSteps = this._rotation / 60;
        return HexMath.rotateDirection(originalDirection, rotationSteps);
    }

    /**
     * 获取当前边在原始坐标系中的方向
     * @param currentDirection 当前显示的方向
     */
    getOriginalDirection(currentDirection: EdgeDirection): EdgeDirection {
        const rotationSteps = -this._rotation / 60;
        return HexMath.rotateDirection(currentDirection, rotationSteps);
    }

    /**
     * 导出状态（用于存档）
     */
    exportState(): PieceState {
        return {
            id: this.id,
            coord: { ...this.coord },
            position: { ...this._position },
            rotation: this._rotation,
            isPlaced: this._isPlaced,
            isInPocket: this._isInPocket,
            groupId: this._groupId ?? undefined
        };
    }

    /**
     * 导入状态（用于读档）
     */
    importState(state: PieceState): void {
        this._position = { ...state.position };
        this._rotation = state.rotation;
        this._isPlaced = state.isPlaced;
        this._isInPocket = state.isInPocket || false;
        this._groupId = state.groupId ?? null;
        this._dirty = true;
    }

    /**
     * 重置到初始状态
     */
    reset(): void {
        this._position = { ...this.correctPosition };
        this._rotation = 0;
        this._isPlaced = false;
        this._groupId = null;
        this._dirty = true;
    }

    /**
     * 计算碎片中心点（考虑当前位置）
     */
    getCenter(): Vector2 {
        // 简化：使用位置作为中心
        // 实际应该计算路径的包围盒中心
        return { ...this._position };
    }

    /**
     * 检查点是否在碎片内部
     * 简化版本：使用矩形包围盒检测
     */
    containsPoint(point: Vector2, size: number = 50): boolean {
        const center = this.getCenter();
        const halfSize = size / 2;

        return point.x >= center.x - halfSize &&
            point.x <= center.x + halfSize &&
            point.y >= center.y - halfSize &&
            point.y <= center.y + halfSize;
    }
}
